import { appInfoSelector } from '@common/redux/selectors/app';
import { localeSelector } from '@common/redux/selectors/language';
import { IPassword } from '@common/types/element';
import {
  buildPolicyMessage,
  getFailedPasswordRules,
  isPasswordPolicyActive,
} from '@common/utils/passwordPolicy';
import { get } from 'lodash';
import React, { FC, useMemo } from 'react';
import { Text, TextInput, TouchableOpacity, View } from 'react-native';
import { useSelector } from 'react-redux';
import connectorInput from '../ConnectorInput';
import createStyles from './style';

const sanitizeValue = (value: any) => {
  return typeof value === 'string' ? value : '';
};

const checkPlaceholder = (value: any) => {
  if (Array.isArray(value)) {
    return value[0];
  } else {
    return value;
  }
};

const Password: FC<IPassword> = (attributes) => {
  const { changeInput, valueInput } = attributes;
  // `inputError` is injected at runtime by the connectorInput HOC.
  const inputError = (attributes as any).inputError as string | undefined;
  const styles = createStyles(attributes);
  const appInfo = useSelector(appInfoSelector);
  const locale = useSelector(localeSelector);

  const value = sanitizeValue(valueInput);
  const passwordPolicy = get(appInfo, 'metadata.passwordPolicy');
  // Client-side feedback: surface the policy requirement while the entered
  // value is non-empty and does not satisfy the active policy. Memoized so the
  // regex checks only re-run when this field's value/policy/locale changes, not
  // on unrelated re-renders (formValueSelector is screen-wide).
  const policyError = useMemo(() => {
    if (!isPasswordPolicyActive(passwordPolicy) || !value) return '';
    const failed = getFailedPasswordRules(value, passwordPolicy);
    return failed.length
      ? buildPolicyMessage(
          failed,
          passwordPolicy,
          locale === 'ja' ? 'ja' : 'en'
        )
      : '';
  }, [value, passwordPolicy, locale]);
  const errorMessage = policyError || inputError;

  return (
    <View>
      <TouchableOpacity activeOpacity={1} style={styles.container}>
        <TextInput
          onChangeText={changeInput}
          value={value}
          style={[
            styles.text,
            {
              display: 'flex',
              alignSelf: 'center',
              textAlignVertical: 'center',
            },
          ]}
          placeholder={checkPlaceholder(attributes.placeholder)}
          placeholderTextColor={attributes.placeholderColor}
          secureTextEntry={true}
        />
      </TouchableOpacity>
      {errorMessage ? (
        <Text style={styles.errorMessage}>{errorMessage}</Text>
      ) : null}
    </View>
  );
};

export default connectorInput(Password);
